import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { listReferrals, setReferralStatus, resendReferralToGhl } from "@/lib/api";
import { toast } from "sonner";
import { Phone } from "lucide-react";

const TONE = { joined: "bg-emerald-500/15 text-emerald-300", consult: "bg-violet-500/15 text-violet-300",
  contacted: "bg-amber-500/15 text-amber-300", invited: "bg-sky-500/15 text-sky-300", not_interested: "bg-zinc-700/40 text-zinc-400" };
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** Friends members referred from the app. Call them as the member's friend; a consult with their
 *  number links itself, and the member earns points when it's sold. */
export default function MemberReferrals() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => listReferrals().then(setData).catch(() => { toast.error("Couldn't load referrals"); setData({ referrals: [] }); }), []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const resend = async (r) => {
    try {
      const { ghl } = await resendReferralToGhl(r.id);
      (["created", "moved", "member"].includes(ghl.state) ? toast.success : toast.error)(ghl.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach GoHighLevel");
    }
  };

  const mark = async (r, status) => {
    try {
      await setReferralStatus(r.id, status);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    }
  };

  return (
    <div className="space-y-6" data-testid="member-referrals">
      <div>
        <div className="eyebrow">Member App · Growth</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Referrals</h2>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          Friends that members invited from the app, for this gym. Each one also goes into this gym&apos;s GoHighLevel pipeline in
          the &quot;Referral&quot; stage, which starts the referral automation. Call each one once, kindly, and say their friend thought of them. When they come in,
          the consult picks up who referred them from their phone number, and the member gets {data?.rewards?.points ?? 500} reward
          points (off their renewal) once it&apos;s sold.
        </p>
      </div>
      {data === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : data.referrals.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">No referrals yet.</div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500">
              <tr className="border-b border-[#1c1c1c]">
                <th className="px-4 py-3">Friend</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Referred by</th>
                <th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">GoHighLevel</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.referrals.map((r) => (
                <tr key={r.id} className="border-b border-[#161616]">
                  <td className="px-4 py-3 font-semibold text-zinc-100">{r.friend_name}<span className="ml-2 text-xs font-normal text-zinc-500">{r.relation}</span></td>
                  <td className="px-4 py-3"><a href={`tel:+${r.friend_phone}`} className="flex items-center gap-1 text-zinc-300 hover:text-white"><Phone className="h-3 w-3" />+{r.friend_phone}</a></td>
                  <td className="px-4 py-3 text-zinc-300">{r.referrer_name}
                    {r.referrer_gym_name && r.referrer_gym_name !== r.gym_name && <div className="text-[11px] text-zinc-500">member at {r.referrer_gym_name}</div>}
                    {r.friend_email && <div className="text-[11px] text-zinc-500">{r.friend_email}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TONE[r.status]}`}>{r.status_label}</span></td>
                  <td className="px-4 py-3 text-xs">
                    <div className={["created", "moved"].includes(r.ghl?.state) ? "text-emerald-300" : r.ghl?.state === "error" || r.ghl?.state === "no_stage" ? "text-red-300" : "text-zinc-500"}>
                      {r.ghl ? r.ghl.message : "Sending…"}
                    </div>
                    {r.ghl && !["created", "moved", "member"].includes(r.ghl.state) && (
                      <button type="button" onClick={() => resend(r)} className="text-zinc-400 hover:underline">Send again</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap">
                    {["invited", "contacted", "not_interested"].includes(r.status) && (
                      <>
                        {r.status !== "contacted" && <button type="button" onClick={() => mark(r, "contacted")} className="text-amber-300 hover:underline">Called</button>}
                        {r.status !== "not_interested" && <button type="button" onClick={() => mark(r, "not_interested")} className="ml-3 text-zinc-400 hover:underline">Not interested</button>}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
